import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface Variant {
  variantKey: string;
  label: string | null;
  weight: number;
}
interface Experiment {
  id: string;
  kind: string;
  status: string;
  contextKey: string | null;
  variants: Variant[];
}
interface BlockRow {
  variantKey: string;
  locale: string;
  position: number;
  blockType: string;
  config: Record<string, unknown>;
}
interface TargetingRule {
  priority: number;
  predicate: Record<string, unknown>;
  forceVariant: string;
  active: boolean;
}
interface StatRow {
  variant: string;
  context: string;
  event: string;
  count: number;
}

const LOCALES = ['es', 'en', 'pt-BR'] as const;
const BLOCK_TYPES = ['HERO', 'TEXT', 'IMAGE', 'VIDEO', 'CTA', 'FORM', 'SPACER', 'HTML'] as const;

/**
 * Story 109.1b — authoring for the marketing-automation suite:
 * experiment kind, N variants, CMS blocks per variant/locale,
 * per-visitor targeting rules, and the live impression/conversion
 * report. All copy is i18n'd (landing.exp.*).
 */
export function LandingExperimentPanel({ pageId }: { pageId: string }) {
  const { t } = useTranslation();
  const [exp, setExp] = useState<Experiment | null>(null);
  const [kind, setKind] = useState('MULTIVARIATE');
  const [status, setStatus] = useState('DRAFT');
  const [contextKey, setContextKey] = useState('');
  const [variants, setVariants] = useState<Variant[]>([]);
  const [stats, setStats] = useState<StatRow[]>([]);
  const [blocks, setBlocks] = useState<BlockRow[]>([]);
  const [rules, setRules] = useState<TargetingRule[]>([]);
  const [blkVariant, setBlkVariant] = useState('a');
  const [blkLocale, setBlkLocale] = useState('es');

  const load = useCallback(() => {
    apiClient
      .get<Experiment>(`/landing-pages/${pageId}/experiment`)
      .then(({ data }) => {
        if (!data) return;
        setExp(data);
        setKind(data.kind);
        setStatus(data.status);
        setContextKey(data.contextKey ?? '');
        setVariants(data.variants ?? []);
      })
      .catch(() => {});
    apiClient
      .get<StatRow[]>(`/landing-pages/${pageId}/experiment/stats`)
      .then(({ data }) => setStats(data))
      .catch(() => {});
    apiClient
      .get<BlockRow[]>(`/landing-pages/${pageId}/experiment/blocks`)
      .then(({ data }) => setBlocks(data))
      .catch(() => {});
    apiClient
      .get<TargetingRule[]>(`/landing-pages/${pageId}/experiment/targeting`)
      .then(({ data }) => setRules(data))
      .catch(() => {});
  }, [pageId]);

  useEffect(() => load(), [load]);

  async function saveExperiment() {
    await apiClient.put(`/landing-pages/${pageId}/experiment`, {
      kind,
      status,
      contextKey: contextKey.trim() || null,
    });
    await apiClient.put(
      `/landing-pages/${pageId}/experiment/variants`,
      variants.map((v) => ({
        variantKey: v.variantKey,
        label: v.label,
        weight: v.weight,
      })),
    );
    load();
  }

  const shownBlocks = blocks
    .filter((b) => b.variantKey === blkVariant && b.locale === blkLocale)
    .sort((a, b) => a.position - b.position);

  async function saveBlocks(next: BlockRow[]) {
    await apiClient.put(`/landing-pages/${pageId}/experiment/blocks`, {
      variantKey: blkVariant,
      locale: blkLocale,
      blocks: next.map((b, i) => ({
        variantKey: blkVariant,
        locale: blkLocale,
        position: i,
        blockType: b.blockType,
        config: b.config,
      })),
    });
    load();
  }

  async function saveRules(next: TargetingRule[]) {
    await apiClient.put(`/landing-pages/${pageId}/experiment/targeting`, next);
    load();
  }

  return (
    <div className="space-y-6">
      <hr className="border-border" />
      <h3 className="text-lg font-semibold">{t('landing.exp.title')}</h3>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-sm">
          {t('landing.exp.kind')}
          <select
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="MULTIVARIATE">{t('landing.exp.multivariate')}</option>
            <option value="BANDIT">{t('landing.exp.bandit')}</option>
          </select>
        </label>
        <label className="text-sm">
          {t('landing.exp.status')}
          <select
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="DRAFT">{t('landing.exp.draft')}</option>
            <option value="RUNNING">{t('landing.exp.running')}</option>
            <option value="STOPPED">{t('landing.exp.stopped')}</option>
          </select>
        </label>
        <label className="text-sm">
          {t('landing.exp.contextKey')}
          <Input
            value={contextKey}
            placeholder="utm_source"
            onChange={(e) => setContextKey(e.target.value)}
          />
        </label>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{t('landing.exp.variants')}</span>
          <Button
            variant="ghost"
            onClick={() =>
              setVariants((v) => [
                ...v,
                { variantKey: String.fromCharCode(97 + v.length), label: '', weight: 1 },
              ])
            }
          >
            {t('landing.exp.addVariant')}
          </Button>
        </div>
        {variants.map((v, i) => (
          <div key={i} className="flex gap-2">
            <Input
              className="w-20"
              value={v.variantKey}
              onChange={(e) =>
                setVariants((arr) =>
                  arr.map((x, j) => (j === i ? { ...x, variantKey: e.target.value } : x)),
                )
              }
            />
            <Input
              placeholder={t('landing.exp.label')}
              value={v.label ?? ''}
              onChange={(e) =>
                setVariants((arr) =>
                  arr.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                )
              }
            />
            <Input
              className="w-24"
              type="number"
              value={v.weight}
              onChange={(e) =>
                setVariants((arr) =>
                  arr.map((x, j) =>
                    j === i ? { ...x, weight: Number(e.target.value) } : x,
                  ),
                )
              }
            />
            <Button
              variant="ghost"
              onClick={() => setVariants((arr) => arr.filter((_, j) => j !== i))}
            >
              ×
            </Button>
          </div>
        ))}
      </div>
      <Button onClick={saveExperiment} data-testid="exp-save">
        {t('landing.exp.save')}
      </Button>

      <div className="space-y-2">
        <span className="text-sm font-medium">{t('landing.exp.blocks')}</span>
        <div className="flex gap-2">
          <select
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={blkVariant}
            onChange={(e) => setBlkVariant(e.target.value)}
          >
            {(variants.length ? variants.map((v) => v.variantKey) : ['a']).map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={blkLocale}
            onChange={(e) => setBlkLocale(e.target.value)}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            onClick={() =>
              setBlocks((b) => [
                ...b,
                {
                  variantKey: blkVariant,
                  locale: blkLocale,
                  position: shownBlocks.length,
                  blockType: 'TEXT',
                  config: {},
                },
              ])
            }
          >
            {t('landing.exp.addBlock')}
          </Button>
        </div>
        {shownBlocks.map((b, i) => (
          <div key={i} className="flex gap-2">
            <select
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
              value={b.blockType}
              onChange={(e) =>
                setBlocks((arr) =>
                  arr.map((x) =>
                    x === b ? { ...x, blockType: e.target.value } : x,
                  ),
                )
              }
            >
              {BLOCK_TYPES.map((bt) => (
                <option key={bt} value={bt}>
                  {bt}
                </option>
              ))}
            </select>
            <textarea
              className="flex-1 rounded-md border border-border bg-background px-2 py-1 font-mono text-xs"
              rows={2}
              value={JSON.stringify(b.config)}
              onChange={(e) => {
                try {
                  const cfg = JSON.parse(e.target.value) as Record<string, unknown>;
                  setBlocks((arr) =>
                    arr.map((x) => (x === b ? { ...x, config: cfg } : x)),
                  );
                } catch {
                  /* keep typing until valid JSON */
                }
              }}
            />
          </div>
        ))}
        <Button onClick={() => saveBlocks(shownBlocks)} data-testid="exp-save-blocks">
          {t('landing.exp.saveBlocks')}
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{t('landing.exp.targeting')}</span>
          <Button
            variant="ghost"
            onClick={() =>
              setRules((r) => [
                ...r,
                { priority: 100, predicate: { all: [] }, forceVariant: 'a', active: true },
              ])
            }
          >
            {t('landing.exp.addRule')}
          </Button>
        </div>
        {rules.map((r, i) => (
          <div key={i} className="flex gap-2">
            <Input
              className="w-20"
              type="number"
              value={r.priority}
              onChange={(e) =>
                setRules((arr) =>
                  arr.map((x, j) =>
                    j === i ? { ...x, priority: Number(e.target.value) } : x,
                  ),
                )
              }
            />
            <textarea
              className="flex-1 rounded-md border border-border bg-background px-2 py-1 font-mono text-xs"
              rows={2}
              value={JSON.stringify(r.predicate)}
              onChange={(e) => {
                try {
                  const pred = JSON.parse(e.target.value) as Record<string, unknown>;
                  setRules((arr) =>
                    arr.map((x, j) => (j === i ? { ...x, predicate: pred } : x)),
                  );
                } catch {
                  /* keep typing until valid JSON */
                }
              }}
            />
            <Input
              className="w-20"
              value={r.forceVariant}
              onChange={(e) =>
                setRules((arr) =>
                  arr.map((x, j) =>
                    j === i ? { ...x, forceVariant: e.target.value } : x,
                  ),
                )
              }
            />
            <Button
              variant="ghost"
              onClick={() => setRules((arr) => arr.filter((_, j) => j !== i))}
            >
              ×
            </Button>
          </div>
        ))}
        <Button onClick={() => saveRules(rules)} data-testid="exp-save-rules">
          {t('landing.exp.saveRules')}
        </Button>
      </div>

      <div>
        <span className="text-sm font-medium">{t('landing.exp.report')}</span>
        {exp == null ? (
          <p className="text-sm text-muted-foreground">{t('landing.exp.none')}</p>
        ) : (
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th>{t('landing.exp.colVariant')}</th>
                <th>{t('landing.exp.colContext')}</th>
                <th>{t('landing.exp.colEvent')}</th>
                <th>{t('landing.exp.colCount')}</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s, i) => (
                <tr key={i}>
                  <td>{s.variant}</td>
                  <td>{s.context}</td>
                  <td>{s.event}</td>
                  <td>{s.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
